/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { checkScope, formatRupiah, isEmptyData, sendAnalyticsEvent, sortVouchers } from '../../utils/helper';
import { StarIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/solid';
import FoodImg from '../../common/images/food.png';
import ImageLoader from '../ImageLoader';
import { TagIcon } from '../../utils/svg';
import { useDispatch, useSelector } from 'react-redux';
import { AgeVerification } from '../AgeVerification';
import { AGE_VERIFICATION_STATUS } from '../../utils/constants';
import { setUser } from '../../store/reducers/user';

const ProductName = ({ name, longestName }) => {
	const nameRef = useRef(null);
	const [maxWidth, setMaxWidth] = useState(0);
	const [isTwoLines, setIsTwoLines] = useState(false);
	const canvasRef = useRef(null);
  if (!canvasRef.current) {
    canvasRef.current = document.createElement('canvas');
  }

	useEffect(() => {
		if (!nameRef?.current) return;
		const resizeObserver = new ResizeObserver(() => {
			setMaxWidth(nameRef?.current?.offsetWidth);
		});

		resizeObserver?.observe(nameRef?.current);

		return () => resizeObserver?.disconnect();
	}, []);

	useEffect(() => {
		if (!maxWidth) return;
		const context = canvasRef?.current.getContext('2d');
		const textWidth = context?.measureText(longestName || name).width;

		setIsTwoLines(textWidth > maxWidth);
	}, [name, longestName, maxWidth]);

	return (
		<div ref={nameRef} className='flex flex-col flex-1'>
			<div className={`text-gray-800 text-xs font-semibold mt-1 ${isTwoLines ? 'line-clamp-2 min-h-[2rem]' : ''}`}>{name}</div>
		</div>
	);
};

const ProductDetail = (props) => {
	const {
		id = 0,
		slug = '',
		image = FoodImg,
		rating = 0,
		totalRating = 0,
		name = '',
		price = 0,
		strikeOutPrice = 0,
		size = 'big',
		handleGetProductDetail = (slug) => {},
		productType = '1',
		cart = null,
		handleCart = (data) => {},
		groupedColumns = [],
		idx = 0,
		disabled = false,
    data = {},
    outlet = {}
	} = props;

  const dispatch = useDispatch();
  const [diskon, setDiskon] = useState(0);
  const user = useSelector((state) => state?.user);
  const [isOpenAgeValidation, setIsOpenAgeValidation] = useState(false);

  const currentCartItems = cart?.item?.filter((item) => item.productID === id) || [];
  const currentCartItem = cart?.item?.find((item) => item.productID === id) || null;
  const currentCartQuantity = currentCartItems.reduce((total, item) => total + Number(item.itemQuantity || 0), 0);

  const handleClick = async (productType, type, id, slug) => {
    console.log('handleClick');
		if (disabled) return;

		if (productType === '2' && Number(currentCartQuantity) > 0) {
			return handleGetProductDetail(slug);
		}
		let prevTotal = Number(currentCartQuantity);
		if (type === 'plus') {
			prevTotal = prevTotal + 1;
		} else {
			prevTotal = prevTotal !== 0 ? prevTotal - 1 : 0;
		}

		const dataCart = {
			productID: id,
			quantity: prevTotal,
			type: !cart ? 'add' : 'update',
			productType: productType === '1' ? 'Simple' : 'Combo',
			itemID: currentCartItem?.itemID || null,
			specialNotes: currentCartItem?.itemNotes ?? '',
      productPrimaryID: currentCartItem?.product_primary_id ?? null
		};

		handleCart(dataCart);
	};

	const rows = (idx) => {
		const foundRow = groupedColumns?.find((group) => group.group.includes(idx));
		return foundRow ? foundRow : {};
	};

	const row = rows(idx);

  const validationVoucherGlobal = async (voucher = {}, product = null) => {
		let nominal_voucher = 0;

		const matchCompany = checkScope(voucher?.company_scope_ids, user?.companyID);
		const matchBrand = checkScope(voucher?.brand_scope_ids, cart?.store?.outletBrandID || outlet?.brand_id);
		const matchOutlet = checkScope(voucher?.outlet_scope_ids, cart?.store?.storeID || outlet?.storeID);
		const matchUser = checkScope(voucher?.user_scope_ids, user?.customerID);

    if (!matchCompany || !matchBrand || !matchOutlet || !matchUser) {
      return { nominal_voucher };
    };

		if (matchCompany || matchBrand || matchOutlet || matchUser) {
			const price = Number(cart?.subTotal);

			if (voucher.type === 'nominal') {
				nominal_voucher = voucher.value;
			}

			if (voucher.type === 'percentage') {
				nominal_voucher = (price * voucher.value) / 100;
			}
		}

		if (!isEmptyData(voucher?.categories_scope_ids) || !isEmptyData(voucher?.product_scope_ids)) {
			nominal_voucher = 0;

      const matchCategory = checkScope(voucher?.categories_scope_ids, product?.product_category_id);
      const matchProduct = checkScope(voucher?.product_scope_ids, product?.product_primary_id);

      if (!matchCategory || !matchProduct) {
        return { nominal_voucher };
      }

      let discount = 0;

      if (matchCategory && isEmptyData(voucher?.product_scope_ids)) {
        let productPrice = Number(currentCartItem?.itemUnitPrice || product?.product_price);
        productPrice = productPrice * Number(currentCartQuantity || 1)

        if (voucher?.type === 'nominal') {
          discount = voucher.value;
        }
        if (voucher?.type === 'percentage') {
          discount = (Number(productPrice) * voucher.value) / 100;
          if (discount > voucher?.max_discount) {
            discount = voucher?.max_discount;
          }
        }
      }

      if (matchProduct && !isEmptyData(voucher?.product_scope_ids)) {
        discount = 0;
        let productPrice = Number(currentCartItem?.itemUnitPrice || product?.product_price);
        productPrice = productPrice * Number(currentCartQuantity || 1)

        if (voucher?.type === 'nominal') {
          discount = voucher.value;
        }
        if (voucher?.type === 'percentage') {
          discount = (Number(productPrice) * voucher.value) / 100;
          if (discount > voucher?.max_discount) {
            discount = voucher?.max_discount;
          }
        }
      }

      nominal_voucher += discount;
		} else {
      nominal_voucher = 0;
			let discount = 0;
			let productPrice = Number(currentCartItem?.itemUnitPrice || product?.product_price);
      productPrice = productPrice * Number(currentCartQuantity || 1)

			if (voucher?.type === 'nominal') {
				discount = voucher?.value;
			}
			if (voucher?.type === 'percentage') {
        discount = (Number(productPrice) * voucher.value) / 100;
        if (discount > voucher?.max_discount) {
          discount = voucher?.max_discount;
        }
			}
      nominal_voucher += discount;
		}

    nominal_voucher = Math.round(Math.min(nominal_voucher, voucher?.type === "nominal" ? voucher?.value : voucher.max_discount ?? nominal_voucher));
    return { nominal_voucher };
  };

  const applyBestVoucher = (vouchers = []) => {
    if (!vouchers.length) return [];
    const maxVoucher = Math.max(...vouchers.map(v => v.nominal_voucher || 0));
    const bestIndex = vouchers.findIndex(v => v.nominal_voucher === maxVoucher && maxVoucher > 0);
    let picked = false;

    return vouchers.map((voucher, index) => {
      if (index === bestIndex && !picked) {
        picked = true;
        return voucher;
      }
      return { ...voucher, nominal_voucher: 0 };
    });
  };

  useEffect(() => {
    const runValidation = async () => {
      const vouchersList = [];
      for (let vg of data?.voucher_global || []) {
        let validation = {}
        if (!vg?.referral) {
          validation = await validationVoucherGlobal(vg, data);
         }
        vouchersList?.push({ ...vg, ...validation });
      }
      const sorted = sortVouchers(vouchersList);
      const result = applyBestVoucher(sorted);
      setDiskon(result?.[0]?.nominal_voucher || 0);
    };

    if (data) {
      runValidation();
    }
  }, [data]);

  const handleOpenAgeVerification = () => {
		dispatch(setUser({ age_verified: AGE_VERIFICATION_STATUS.PROMPT }));
    setIsOpenAgeValidation(true);
  }

  const handleCloseAgeVerification = () => {
    setIsOpenAgeValidation(false);
  }

  const ageVerificationProps = {
    isOpen: isOpenAgeValidation,
    callback: handleCloseAgeVerification
  }

  const deniedAgeVerification = Boolean(Number(data?.adult_product || 0)) && user?.age_verified === AGE_VERIFICATION_STATUS.DENIED;
  const adultValidation = Boolean(Number(data?.adult_product || 0)) && user?.age_verified === AGE_VERIFICATION_STATUS.UNKNOWN;

	return (
    <>
      <div
        key={id}
        className={`${size === 'big' ? 'h-full w-full ' : 'flex flex-col min-w-44'} !z-0 ${disabled ? 'opacity-60' : ''}`}
        onClick={
          disabled || deniedAgeVerification
            ? null
            : () => {
                sendAnalyticsEvent('Click Product', 'merchant_add_product', name);
                if (adultValidation) {
                  return handleOpenAgeVerification();
                }
                handleGetProductDetail();
              }
        }
        id='merchant_add_product'
        style={{ cursor: disabled || deniedAgeVerification ? 'not-allowed' : 'pointer' }}>
        <div className={`aspect-[1/1] relative ${size === 'big' ? 'w-full h-auto' : 'max-h-36 max-w-36'}`}>
          <ImageLoader alt={slug} src={image} style={disabled || deniedAgeVerification ? { filter: 'grayscale(100%)', opacity: 0.5 } : {}} priority={idx < 6} />

          <div className='absolute bottom-0 right-0 m-2 z-10'>
            {Number(currentCartQuantity || 0) ? (
              <div className={`bg-white p-1 rounded-md flex items-center border-solid border-1 border-[var(--byzantium)] ${disabled || deniedAgeVerification ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <MinusIcon
                  className={`h-5 w-5 ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-[var(--byzantium)]'}`}
                  onClick={(event) => {
                    if (disabled || deniedAgeVerification) return;
                    event.stopPropagation();
                    handleClick(productType, 'minus', id, slug);
                  }}
                />
                <input type='text' className='!bg-white !m-0 !p-0 !h-5 !w-8 text-center font-semibold !text-base opacity-100' value={Number(currentCartQuantity || 0)} disabled={disabled || deniedAgeVerification} />
                <PlusIcon
                  className={`h-5 w-5 ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-[var(--byzantium)]'}`}
                  onClick={(event) => {
                    if (disabled || deniedAgeVerification) return;
                    event.stopPropagation();
                    handleClick(productType, 'plus', id, slug);
                  }}
                />
              </div>
            ) : (
              <div className={`bg-white p-1 rounded-md border-1 border-solid border-[var(--byzantium)] ${disabled || deniedAgeVerification ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <PlusIcon className={`h-5 w-5 ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-[var(--byzantium)]'}`} />
              </div>
            )}
          </div>
        </div>

        <div className={`flex gap-1 items-center pt-2 ${disabled || deniedAgeVerification ? 'opacity-50' : ''}`}>
          <StarIcon className={`h-4 w-4 ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-star'}`} />
          <div className={`text-xs ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-black'}`}>{rating}</div>
          <div className={`text-xs ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-gray-900'}`}>{totalRating && `(${totalRating})`}</div>
        </div>

        <div className={disabled || deniedAgeVerification ? 'opacity-50' : ''}>
          <ProductName name={name} longestName={row?.longest_name} disabled={disabled || deniedAgeVerification} />
        </div>

        <div className={`text-xs flex items-center pt-1 mb-3 ${disabled || deniedAgeVerification ? 'opacity-50' : ''}`}>
          <div className={`font-semibold pr-1 ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-gray-800'}`}>{formatRupiah(price)}</div>
          {Boolean(strikeOutPrice) && <div className={`line-through ${disabled || deniedAgeVerification ? 'text-gray-400' : 'text-gray-500'}`}>{formatRupiah(strikeOutPrice)}</div>}
        </div>

        {diskon > 0 && (
          <div className='mt-1 py-1 px-2 bg-green-100/50 rounded-md w-fit flex items-center text-xxs font-medium text-green-600'>
            <TagIcon className='h-3 w-3 fill-green-600 rotate-0 mr-1' />
            {formatRupiah(diskon)} off
          </div>
        )}
      </div>
      <AgeVerification {...ageVerificationProps} />
    </>
	);
};

const MemoizedProductDetail = React.memo(ProductDetail);

const COLUMN_COUNT = 2;
const DEFAULT_ROW_HEIGHT_GUESS = 280;
const DEFAULT_OVERSCAN_ROWS = 3;

const Row = React.memo(function Row({
  rowIndex,
  top,
  rowProducts,
  startIdx,
  outlet,
  cart,
  handleCart,
  handleGetProductDetail,
  groupedColumns,
  onMeasure,
}) {
  const rowRef = useRef(null);

  useEffect(() => {
    if (!rowRef.current) return;
    const el = rowRef.current;

    onMeasure(rowIndex, el.getBoundingClientRect().height);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        onMeasure(rowIndex, entry.contentRect.height);
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [rowIndex]);

  return (
    <div
      ref={rowRef}
      style={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '16px',
      }}
    >
      {rowProducts.map((product, colIdx) => {
        const index = startIdx + colIdx;
        const isInactive = !product?.product_status || product?.product_status === 'I';

        return (
          <MemoizedProductDetail
            key={`feature_${product?.product_id}_${index}`}
            id={product?.product_id}
            slug={product?.product_slug}
            image={product?.product_thumbnail}
            rating={outlet?.rating || outlet?.Rating}
            totalRating={outlet?.totalRating}
            name={product?.product_name}
            price={Number(product?.product_price)}
            strikeOutPrice={Number(product?.product_cost)}
            productType={product?.product_type}
            handleGetProductDetail={() => handleGetProductDetail(product)}
            cart={cart}
            handleCart={handleCart}
            groupedColumns={groupedColumns}
            idx={index}
            disabled={isInactive}
            data={product}
            outlet={outlet}
          />
        );
      })}
    </div>
  );
});

function findRowAtOffset(offsets, target) {
  let lo = 0;
  let hi = offsets.length - 2;
  if (hi < 0) return 0;

  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (offsets[mid] <= target) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}

export const ProductGrid = ({
  products = [],
  outlet = {},
  cart = null,
  handleCart = () => {},
  handleGetProductDetail = () => {},
  overscanRows = DEFAULT_OVERSCAN_ROWS,
}) => {
  const wrapperRef = useRef(null);
  const rafRef = useRef(null);
  const measureRafRef = useRef(null);
  const rowHeightsRef = useRef(new Map());
  const [heightVersion, setHeightVersion] = useState(0);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });

  const groupedColumns = useMemo(() => {
    if (!products?.length) return [];
    return Array.from({ length: Math.ceil(products.length / COLUMN_COUNT) }, (_, i) => {
      const group = [i * COLUMN_COUNT, i * COLUMN_COUNT + 1].filter((idx) => idx < products.length);
      const names = group?.map((idx) => products?.[idx]?.product_name);
      const longest_name = names?.reduce(
        (longest, name) => (name?.length > longest?.length ? name : longest),
        ''
      );
      return { group, names, longest_name };
    });
  }, [products]);

  const rowCount = Math.ceil(products.length / COLUMN_COUNT);

  const estimatedRowHeight = useMemo(() => {
    const heights = Array.from(rowHeightsRef.current.values());
    if (!heights.length) return DEFAULT_ROW_HEIGHT_GUESS;
    return heights.reduce((a, b) => a + b, 0) / heights.length;
  }, [heightVersion]);

  const offsets = useMemo(() => {
    const arr = [0];
    for (let i = 0; i < rowCount; i++) {
      const h = rowHeightsRef.current.get(i) ?? estimatedRowHeight;
      arr.push(arr[i] + h);
    }
    return arr;
  }, [rowCount, heightVersion, estimatedRowHeight]);

  const handleMeasure = (rowIndex, height) => {
    if (!height || height <= 0) return;
    const prev = rowHeightsRef.current.get(rowIndex);
    if (prev && Math.abs(prev - height) < 1) return;

    rowHeightsRef.current.set(rowIndex, height);

    if (!measureRafRef.current) {
      measureRafRef.current = requestAnimationFrame(() => {
        measureRafRef.current = null;
        setHeightVersion((v) => v + 1);
      });
    }
  };

  useEffect(() => {
    const computeRange = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const wrapperTop = rect.top + (window.scrollY || document.documentElement.scrollTop);
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;

      const relativeScroll = Math.max(0, scrollY - wrapperTop);
      const relativeBottom = relativeScroll + viewportHeight;

      let start = findRowAtOffset(offsets, relativeScroll) - overscanRows;
      let end = findRowAtOffset(offsets, relativeBottom) + overscanRows;

      start = Math.max(0, start);
      end = Math.min(Math.max(rowCount - 1, 0), end);

      setVisibleRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };

    const onScrollOrResize = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        computeRange();
      });
    };

    computeRange();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [rowCount, offsets, overscanRows]);

  if (!products?.length) return null;

  const rowsToRender = [];
  for (let rowIndex = visibleRange.start; rowIndex <= visibleRange.end; rowIndex++) {
    const startIdx = rowIndex * COLUMN_COUNT;
    const rowProducts = [products[startIdx], products[startIdx + 1]].filter(Boolean);
    if (!rowProducts.length) continue;

    rowsToRender.push(
      <Row
        key={rowIndex}
        rowIndex={rowIndex}
        top={offsets[rowIndex]}
        rowProducts={rowProducts}
        startIdx={startIdx}
        outlet={outlet}
        cart={cart}
        handleCart={handleCart}
        handleGetProductDetail={handleGetProductDetail}
        groupedColumns={groupedColumns}
        onMeasure={handleMeasure}
      />
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: offsets[rowCount] || 0 }} className="w-full">
      {rowsToRender}
    </div>
  );
};

export default MemoizedProductDetail;